import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setScheduleData } from '@/redux/slices/focusNewScheduleSlice';
import { selectFocusNewScheduleSlice } from '@/redux/selectors/focusNewScheduleSelectors';
import { territoryGrouping } from '@/components/Focus/constants';
import ScheduleSelectedAccordion from '@/components/Focus/AvailsSchedule/NewSchedule/ScheduleSelectedAccordion';
import NewScheduleAvailItem from '../NewScheduleAvails/NewScheduleAvailItem';

type ScheduleSelectedTerritoriesProps = {
  selectedTerritories: string[];
  readOnly?: boolean;
};

const ScheduleSelectedTerritories = ({ selectedTerritories, readOnly = false }: ScheduleSelectedTerritoriesProps) => {
  const dispatch = useAppDispatch();
  const { rightsPossible } = useAppSelector(selectFocusNewScheduleSlice);
  const [isExpanded, setIsExpanded] = useState(false);
  const selectedCount = selectedTerritories.length;
  const regionNamesInEnglish = useMemo(() => new Intl.DisplayNames(['en'], { type: 'region' }), []);

  const regionRights = useMemo(() => {
    if (!rightsPossible) return [];
    return Object.keys(rightsPossible.territories).map((t) => t.toUpperCase());
  }, [rightsPossible]);

  const removeTerritory = (territory: string) => {
    dispatch(setScheduleData({ selectedTerritories: selectedTerritories.filter((t) => t !== territory) }));
  };

  const region = useMemo(() => {
    return Object.entries(territoryGrouping).map(([groupLabel, territories]) => {
      const commonTerritories = territories.filter((territory) => selectedTerritories.includes(territory.code));
      if (commonTerritories.length === 0) return;
      return {
        label: groupLabel,
        territories: commonTerritories.map((t) => {
          const code = t.code.toUpperCase();
          return { code, label: regionNamesInEnglish.of(code) ?? t.code };
        })
      };
    });
  }, [selectedTerritories, regionNamesInEnglish]);

  return (
    <ScheduleSelectedAccordion expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
      <ScheduleSelectedAccordion.Summary>
        <ScheduleSelectedAccordion.Content>
          <ScheduleSelectedAccordion.Title>Territories</ScheduleSelectedAccordion.Title>
          <ScheduleSelectedAccordion.Caption>
            {selectedCount} {selectedCount > 1 ? 'Territories' : 'Territory'} selected
          </ScheduleSelectedAccordion.Caption>
        </ScheduleSelectedAccordion.Content>
        {!isExpanded && (
          <ScheduleSelectedAccordion.Chips>
            {region &&
              region.map((r) => {
                if (!r) return;
                return (
                  <RegionChip key={r.label}>
                    <Typography>{r.label}</Typography>
                    <Badge>{r.territories.length}</Badge>
                  </RegionChip>
                );
              })}
          </ScheduleSelectedAccordion.Chips>
        )}
      </ScheduleSelectedAccordion.Summary>
      <ScheduleSelectedAccordion.Details>
        {region &&
          region.map((r) => {
            if (!r) return;
            return (
              <RegionItem key={r.label}>
                <RegionDetails>{r.label}</RegionDetails>
                <Territories>
                  {r.territories.map(({ code, label }) => (
                    <TerritoryItem key={code}>
                      <NewScheduleAvailItem
                        isSelectedItem={false}
                        primaryLabel={`${label} [${code}]`}
                        onClick={() => removeTerritory(code)}
                        hasRights={regionRights.includes(code)}
                        disabled={readOnly}
                      />
                    </TerritoryItem>
                  ))}
                </Territories>
              </RegionItem>
            );
          })}
      </ScheduleSelectedAccordion.Details>
    </ScheduleSelectedAccordion>
  );
};

export default ScheduleSelectedTerritories;

const RegionChip = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(1, 1.5),
  border: `1px solid rgba(170, 125, 255, 1)`,
  borderRadius: 6,
  backgroundColor: theme.palette.tertiary.main,
  '& .MuiTypography-root': {
    color: theme.palette.tertiary.contrastText,
    fontWeight: 'semibold'
  }
}));

const Badge = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.tertiary.contrastText,
  color: theme.palette.tertiary.main,
  minWidth: 15,
  minHeight: 15,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: theme.typography.pxToRem(12),
  fontWeight: 'semibold'
}));

const RegionItem = styled('div')``;

const RegionDetails = styled('div')(({ theme }) => ({
  display: 'flex',
  color: '#D9D9D9',
  fontWeight: 500,
  marginBottom: theme.spacing(1)
}));

const Territories = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: theme.spacing(1),
  width: '100%'
}));

const TerritoryItem = styled('div')({
  minWidth: 0
});
